import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Pagination,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
  type StatusIndicatorProps,
  Table,
} from '@cloudscape-design/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { useEffect, useState } from 'react';

import {
  createBulkScorecardJob,
  downloadBulkScorecardJob,
  listBulkScorecardJobs,
  type BulkScorecardJob,
  type BulkScorecardJobPage,
} from '../../common/api/bulkScorecardJobs';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { BulkScorecardUploadModal } from '../../components/dashboard/BulkScorecardUploadModal';

const ACTIVE_STATUSES = new Set<BulkScorecardJob['status']>(['queued', 'processing']);
const STATUS: Record<
  BulkScorecardJob['status'],
  { label: string; type: StatusIndicatorProps.Type }
> = {
  queued: { label: 'Queued', type: 'pending' },
  processing: { label: 'Processing', type: 'in-progress' },
  completed: { label: 'Completed', type: 'success' },
  completed_with_errors: { label: 'Completed with errors', type: 'warning' },
  failed: { label: 'Failed', type: 'error' },
};

export function getBulkScorecardRefetchInterval(
  data: BulkScorecardJobPage | undefined,
): 5000 | false {
  return data?.items.some((job) => ACTIVE_STATUSES.has(job.status)) ? 5000 : false;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

function progressValue(job: BulkScorecardJob): number {
  if (job.total_count === 0) return job.status === 'completed' ? 100 : 0;
  return Math.min(100, Math.round((job.processed_count / job.total_count) * 100));
}

function StatusCell({ job }: { job: BulkScorecardJob }) {
  const status = STATUS[job.status];
  return (
    <SpaceBetween size="xs">
      <StatusIndicator type={status.type}>{status.label}</StatusIndicator>
      <ProgressBar
        value={progressValue(job)}
        status={
          job.status === 'failed' ? 'error' : job.result_available ? 'success' : 'in-progress'
        }
        ariaLabel={`Progress for ${job.input_filename}`}
      />
      {job.error_message ? <Box color="text-status-error">{job.error_message}</Box> : null}
    </SpaceBetween>
  );
}

function CountsCell({ job }: { job: BulkScorecardJob }) {
  return (
    <SpaceBetween size="xxs">
      <Box>{`${job.processed_count} of ${job.total_count} processed`}</Box>
      <Box>{`${job.completed_count} completed`}</Box>
      <Box color={job.invalid_count ? 'text-status-error' : 'text-body-secondary'}>
        {`${job.invalid_count} invalid`}
      </Box>
      <Box color={job.unmatched_count ? 'text-status-error' : 'text-body-secondary'}>
        {`${job.unmatched_count} unmatched`}
      </Box>
      <Box color={job.failed_count ? 'text-status-error' : 'text-body-secondary'}>
        {`${job.failed_count} failed`}
      </Box>
    </SpaceBetween>
  );
}

function useCompactTable(): boolean {
  const [compact, setCompact] = useState(() => window.innerWidth < 720);
  useEffect(() => {
    const update = () => setCompact(window.innerWidth < 720);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return compact;
}

export default function BulkJobsPage() {
  const [page, setPage] = useState(1);
  const [uploadVisible, setUploadVisible] = useState(false);
  const compactTable = useCompactTable();
  const queryClient = useQueryClient();
  const jobsQuery = useQuery({
    queryKey: ['bulk-scorecard-jobs', page, 25],
    queryFn: () => listBulkScorecardJobs(page, 25),
    refetchInterval: (query) => getBulkScorecardRefetchInterval(query.state.data),
  });
  const download = useMutation({
    mutationFn: async (job: BulkScorecardJob) => ({
      blob: await downloadBulkScorecardJob(job.uuid),
      filename: job.result_filename,
    }),
    onSuccess: ({ blob, filename }) => saveAs(blob, filename),
  });
  const items = jobsQuery.data?.items ?? [];

  return (
    <DashboardViewLayout
      breadcrumbs={[{ text: 'Bulk Jobs', href: '/dashboard/bulk-jobs' }]}
      contentType="table"
    >
      <SpaceBetween size="m">
        {jobsQuery.isError && items.length > 0 ? (
          <Alert type="warning" header="Bulk jobs may be out of date">
            Refresh the list to try again.
          </Alert>
        ) : null}
        {download.isError ? (
          <Alert
            type="error"
            header="Download failed"
            dismissible
            onDismiss={() => download.reset()}
          >
            The workbook could not be downloaded. Try again.
          </Alert>
        ) : null}
        <Container>
          <Table
            variant="embedded"
            stickyHeader
            loading={jobsQuery.isLoading}
            loadingText="Loading bulk jobs"
            items={items}
            trackBy="uuid"
            empty={
              <Box color="inherit" textAlign="center" padding={{ vertical: 'l' }}>
                {jobsQuery.isError ? (
                  <SpaceBetween size="s">
                    <Box variant="strong">Bulk jobs could not be loaded</Box>
                    <Box>Try refreshing the list.</Box>
                    <Button onClick={() => jobsQuery.refetch()}>Retry</Button>
                  </SpaceBetween>
                ) : (
                  <SpaceBetween size="s">
                    <Box variant="strong">No bulk jobs yet</Box>
                    <Box>Upload an Excel file to create scorecards.</Box>
                  </SpaceBetween>
                )}
              </Box>
            }
            header={
              <Header
                variant="h1"
                counter={`(${jobsQuery.data?.total ?? 0})`}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      iconName="refresh"
                      loading={jobsQuery.isFetching}
                      onClick={() => jobsQuery.refetch()}
                    >
                      Refresh
                    </Button>
                    <Button variant="primary" onClick={() => setUploadVisible(true)}>
                      Upload Excel file
                    </Button>
                  </SpaceBetween>
                }
              >
                Bulk Jobs
              </Header>
            }
            pagination={
              <Pagination
                currentPageIndex={page}
                pagesCount={jobsQuery.data?.pages ?? 1}
                onChange={({ detail }) => setPage(detail.currentPageIndex)}
              />
            }
            columnDisplay={[
              { id: 'mobile', visible: compactTable },
              { id: 'file', visible: !compactTable },
              { id: 'status', visible: !compactTable },
              { id: 'counts', visible: !compactTable },
              { id: 'result', visible: !compactTable },
            ]}
            columnDefinitions={[
              {
                id: 'mobile',
                header: 'Job',
                cell: (job) => (
                  <SpaceBetween size="s">
                    <SpaceBetween size="xxs">
                      <Box variant="strong">{job.input_filename}</Box>
                      <Box color="text-body-secondary">{formatDate(job.created_at)}</Box>
                    </SpaceBetween>
                    <StatusCell job={job} />
                    <CountsCell job={job} />
                    {job.result_available ? (
                      <Button
                        iconName="download"
                        loading={download.isPending && download.variables?.uuid === job.uuid}
                        onClick={() => download.mutate(job)}
                      >
                        Download
                      </Button>
                    ) : null}
                  </SpaceBetween>
                ),
              },
              {
                id: 'file',
                header: 'File',
                cell: (job) => (
                  <SpaceBetween size="xxs">
                    <Box variant="strong">{job.input_filename}</Box>
                    <Box color="text-body-secondary">{formatDate(job.created_at)}</Box>
                  </SpaceBetween>
                ),
                minWidth: 190,
              },
              {
                id: 'status',
                header: 'Status',
                cell: (job) => <StatusCell job={job} />,
                minWidth: 190,
              },
              {
                id: 'counts',
                header: 'Rows',
                cell: (job) => <CountsCell job={job} />,
                minWidth: 160,
              },
              {
                id: 'result',
                header: 'Result',
                cell: (job) =>
                  job.result_available ? (
                    <Button
                      iconName="download"
                      loading={download.isPending && download.variables?.uuid === job.uuid}
                      onClick={() => download.mutate(job)}
                    >
                      Download
                    </Button>
                  ) : (
                    'Not available'
                  ),
                width: 180,
              },
            ]}
          />
        </Container>
      </SpaceBetween>
      <BulkScorecardUploadModal
        visible={uploadVisible}
        onDismiss={() => setUploadVisible(false)}
        onUpload={async (file) => {
          await createBulkScorecardJob(file);
        }}
        onUploaded={() => queryClient.invalidateQueries({ queryKey: ['bulk-scorecard-jobs'] })}
      />
    </DashboardViewLayout>
  );
}
